import React, { createContext, useContext } from 'react';

import { Typography } from '@mui/material';
import {
  ChartsTooltipContainer,
  useAxesTooltip,
  useItemTooltip,
} from '@mui/x-charts-premium/ChartsTooltip';
import { makeStyles } from 'tss-react/mui';

import type { InsightChartCategory, InsightChartModel } from './insight-chart-series';

const useStyles = makeStyles()(theme => ({
  axisValue: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontWeight: 500,
    padding: theme.spacing(0.75, 1.5),
    whiteSpace: 'nowrap',
  },
  hint: {
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'block',
    padding: theme.spacing(0.75, 1.5),
    whiteSpace: 'nowrap',
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    // The tooltip floats over dense stacked bars, so it needs to read as a layer above them.
    boxShadow: theme.appShadows.popper,
    color: theme.palette.text.primary,
    overflow: 'hidden',
  },
  rows: {
    alignItems: 'center',
    display: 'grid',
    gap: theme.spacing(0.5, 1),
    gridTemplateColumns: 'auto minmax(0, 220px) auto',
    padding: theme.spacing(0.75, 1.5),
  },
  swatch: {
    borderRadius: 2,
    flex: '0 0 auto',
    height: 10,
    width: 10,
  },
  value: {
    fontWeight: 500,
    justifySelf: 'end',
    whiteSpace: 'nowrap',
  },
}));

/**
 * Drill-down Charts explain the click inside the Chart tooltip itself. A second hover
 * tooltip over the plot would collide with it, so the hint travels with the data and
 * the plots only have to declare whether they drill down.
 */
const InsightChartTooltipContext = createContext<{
  drilldown: boolean;
  model: InsightChartModel | null;
}>({ drilldown: false, model: null });

export function InsightChartTooltipProvider({
  children,
  drilldown,
  model = null,
}: {
  children: React.ReactNode;
  drilldown: boolean;
  model?: InsightChartModel | null;
}): JSX.Element {
  return (
    <InsightChartTooltipContext.Provider value={{ drilldown, model }}>
      {children}
    </InsightChartTooltipContext.Provider>
  );
}

export function getInsightTooltipCategoryLabel(
  catalogs: readonly InsightChartCategory[][],
  key: string | undefined,
  fallback: string | null
): string | null {
  if (!key) return fallback;
  for (const catalog of catalogs) {
    const category = catalog.find(item => item.id === key);
    if (category) return category.tooltipLabel ?? category.label;
  }
  return fallback;
}

export function getInsightTooltipAxisLabel(
  model: Pick<InsightChartModel, 'primaryCatalog' | 'xKeys'>,
  dataIndex: number,
  fallback: string
): string {
  return getInsightTooltipAxisLabelForKey(model, model.xKeys.at(dataIndex), fallback);
}

export function getInsightTooltipAxisLabelForKey(
  model: Pick<InsightChartModel, 'primaryCatalog'>,
  key: string | undefined,
  fallback: string
): string {
  return getInsightTooltipCategoryLabel([model.primaryCatalog], key, fallback) ?? fallback;
}

export function getInsightTooltipSeriesLabel(
  model: Pick<InsightChartModel, 'primaryCatalog' | 'secondaryCatalog'>,
  seriesId: string,
  fallback: string | null
): string | null {
  return getInsightTooltipCategoryLabel(
    [model.secondaryCatalog, model.primaryCatalog],
    seriesId,
    fallback
  );
}

/** Tooltip for Charts with a category axis: every series stacked at the hovered category. */
export function InsightAxisTooltip(): JSX.Element {
  return (
    <ChartsTooltipContainer trigger="axis">
      <InsightAxisTooltipContent />
    </ChartsTooltipContainer>
  );
}

/** Tooltip for Charts without an axis (pie): only the hovered slice. */
export function InsightItemTooltip(): JSX.Element {
  return (
    <ChartsTooltipContainer trigger="item">
      <InsightItemTooltipContent />
    </ChartsTooltipContainer>
  );
}

function InsightAxisTooltipContent(): JSX.Element | null {
  const { classes } = useStyles();
  const { model } = useContext(InsightChartTooltipContext);
  const axes = useAxesTooltip();
  if (!axes) return null;

  return (
    <div className={classes.paper}>
      {axes.map(axis => (
        <React.Fragment key={axis.axisId}>
          {!axis.mainAxis.hideTooltip && (
            <Typography className={classes.axisValue} variant="caption">
              {model
                ? getInsightTooltipAxisLabel(model, axis.dataIndex, axis.axisFormattedValue)
                : axis.axisFormattedValue}
            </Typography>
          )}
          <div className={classes.rows}>
            {axis.seriesItems.map(item => (
              <InsightTooltipRow
                key={String(item.seriesId)}
                color={item.color}
                formattedValue={item.formattedValue}
                label={
                  model
                    ? getInsightTooltipSeriesLabel(
                        model,
                        String(item.seriesId),
                        item.formattedLabel
                      )
                    : item.formattedLabel
                }
              />
            ))}
          </div>
        </React.Fragment>
      ))}
      <InsightTooltipHint />
    </div>
  );
}

function InsightItemTooltipContent(): JSX.Element | null {
  const { classes } = useStyles();
  const { model } = useContext(InsightChartTooltipContext);
  const item = useItemTooltip<'pie'>();
  if (!item) return null;

  const sliceKey = typeof item.value.id === 'string' ? item.value.id : undefined;
  const label = model
    ? getInsightTooltipAxisLabelForKey(model, sliceKey, item.label ?? '')
    : (item.label ?? null);

  return (
    <div className={classes.paper}>
      <div className={classes.rows}>
        <InsightTooltipRow
          color={item.color}
          formattedValue={item.formattedValue}
          label={label || null}
        />
      </div>
      <InsightTooltipHint />
    </div>
  );
}

function InsightTooltipRow({
  color,
  formattedValue,
  label,
}: {
  color: string;
  formattedValue: string | null;
  label: string | null;
}): JSX.Element | null {
  const { classes } = useStyles();
  if (!formattedValue) return null;

  return (
    <>
      <span className={classes.swatch} style={{ backgroundColor: color }} />
      <Typography
        className={classes.label}
        color="textSecondary"
        title={label ?? undefined}
        variant="caption"
      >
        {label}
      </Typography>
      <Typography className={classes.value} variant="caption">
        {formattedValue}
      </Typography>
    </>
  );
}

function InsightTooltipHint(): JSX.Element | null {
  const { classes } = useStyles();
  const { drilldown } = useContext(InsightChartTooltipContext);
  if (!drilldown) return null;

  return (
    <Typography className={classes.hint} color="textSecondary" variant="caption">
      Click to view these Workflows
    </Typography>
  );
}
